import styled from "styled-components";

export const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

export const HeroSection = styled.section`
  position: relative;
  height: calc(100dvh - 72px);
  box-sizing: border-box;
  padding: 0 ${({ theme }) => theme.padding.medium};

  @media (min-width: 1025px) and (max-width: 1519px) and (max-height: 768px) {
    height: calc(100dvh - 72px);
    padding-inline: ${({ theme }) => theme.padding.regular};
  }

  @media (max-width: 1024px) {
    height: auto;
    padding: ${({ theme }) => theme.padding.regular}
      ${({ theme }) => theme.padding.regular} 0;
  }

  @media (max-width: 520px) {
    min-height: auto;
    padding: ${({ theme }) => theme.padding.regular} 0 0;
  }

  @media (min-width: 2500px) {
    padding: 0 ${({ theme }) => theme.padding.large}
      ${({ theme }) => theme.padding.large};
  }
  @media (min-width: 3500px) {
    padding: 0 1.5dvw 1.5dvw;
  }
`;

export const MainContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing.medium};
  height: 100%;
  box-sizing: border-box;
  padding-bottom: ${({ theme }) => theme.padding.medium};

  @media (min-width: 1025px) and (max-width: 1519px) and (max-height: 768px) {
    padding-bottom: ${({ theme }) => theme.padding.regular};
  }

  @media (max-width: 1024px) {
    justify-content: flex-start;
    gap: ${({ theme }) => theme.spacing.regular};
    height: auto;
    padding-bottom: 0;
  }
  @media (min-height: 2000px) {
    gap: ${({ theme }) => theme.spacing.large};
    padding-bottom: ${({ theme }) => theme.padding.xlarge};
  }
`;

export const HeaderContainer = styled.div`
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.small};
  width: fit-content;
  margin-bottom: 74px;

  @media (max-width: 1600px) {
    max-width: 49%;
    margin-bottom: ${({ theme }) => theme.spacing.regular};
  }

  @media (min-width: 1281px) and (max-width: 1530px) {
    max-width: 47%;
  }

  @media (max-width: 1280px) {
    max-width: 53%;
  }

  @media (max-width: 1024px) {
    align-self: flex-end;
    max-width: 100%;
    margin-bottom: 0;
  }

  @media (min-width: 2500px) {
    margin-bottom: 40px;
  }
  @media (min-width: 2500px) and (max-height: 1080px) {
    margin-bottom: 10px;
  }
  @media (min-width: 4500px) {
    width: 42dvw;
  }

  @media (min-height: 2000px) {
    margin-bottom: 120px;
  }
`;

export const Header = styled.h2`
  margin: 0;
  font-size: ${({ theme }) => theme.typography.h2.fontSize};
  font-weight: ${({ theme }) => theme.fontWeight.extrabold};
  text-transform: uppercase;
  text-align: right;

  @media (min-width: 1025px) and (max-width: 1519px) and (max-height: 768px) {
    font-size: ${({ theme }) => theme.typography.h3.fontSize};
  }
  @media (max-width: 1440px) {
    font-size: 1.7rem;
  }
  @media (max-width: 754px) {
    font-size: ${({ theme }) => theme.typography.h3.fontSize};
  }
  @media (max-width: 664px) {
    font-size: 1.5rem;
  }
  @media (max-width: 575px) {
    font-size: 1.35rem;
  }
  @media (max-width: 520px) {
    box-sizing: border-box;
    width: 100%;
    padding: 0 ${({ theme }) => theme.padding.small};
    font-size: ${({ theme }) => theme.typography.medium.fontSize};
    line-height: 1.2;
  }

  @media (min-width: 2500px) {
    font-size: 2.75rem;
  }
  @media (min-width: 3000px) {
    font-size: 3rem;
  }
  @media (min-width: 3400px) {
    font-size: 3.5rem;
  }
  @media (min-width: 3800px) {
    font-size: 4rem;
  }
  @media (min-width: 4500px) {
    font-size: 4.75rem;
  }
  @media (min-width: 5000px) {
    font-size: 5.25rem;
  }
`;

export const SubHeader = styled.h3`
  margin: 0;
  font-size: ${({ theme }) => theme.typography.body.fontSize};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  line-height: auto;
  text-transform: uppercase;
  text-align: right;

  @media (max-width: 520px) {
    padding: 0 ${({ theme }) => theme.padding.small};
    font-size: ${({ theme }) => theme.typography.regular.fontSize};
    line-height: 1.3;
  }

  @media (min-width: 2500px) {
    font-size: 1.75rem;
  }
  @media (min-width: 2500px) and (max-height: 1080px) {
    font-size: 1.7rem;
  }
  @media (min-width: 3000px) {
    font-size: 2rem;
  }
  @media (min-width: 3400px) {
    font-size: 2.25rem;
  }
  @media (min-width: 3800px) {
    font-size: 2.5rem;
  }
  @media (min-width: 4500px) {
    font-size: 3.25rem;
  }
  @media (min-width: 5000px) {
    font-size: 3.5rem;
  }
`;

export const MainPicture = styled.div<{ $image: string }>`
  --cutout-x: 42%;
  --cutout-height: 81px;
  --picture-radius: ${({ theme }) => theme.borderRadius.medium};
  --picture-image: url(${({ $image }) => $image});
  position: absolute;
  z-index: 0;
  top: -44px;
  right: 0;
  bottom: ${({ theme }) => theme.padding.medium};
  width: 58%;
  pointer-events: none;

  @media (max-width: 1850px) {
    width: 56%;
  }

  @media (max-width: 1760px) {
    width: 54%;
  }

  @media (max-width: 1660px) {
    width: 52%;
  }

  @media (max-width: 1590px) {
    width: 50%;
  }

  @media (max-width: 1440px) {
    max-width: 46%;
  }

  @media (max-width: 1280px) {
    max-width: 44%;
  }

  @media (max-width: 1180px) {
    max-width: 40%;
  }

  @media (max-width: 1100px) {
    max-width: 38%;
  }

  @media (max-width: 1024px) {
    position: relative;
    top: auto;
    right: auto;
    bottom: auto;
    max-width: 100%;
    width: 100%;
    height: auto;
    aspect-ratio: 1.26 / 1;
    order: 2;
  }

  @media (max-width: 520px) {
    aspect-ratio: 0.8;
  }
  @media (max-width: 380px) {
    aspect-ratio: 0.85;
  }

  @media (min-width: 2100px) {
    width: 58dvw;
  }
  @media (min-width: 2200px) {
    width: 60dvw;
  }
  @media (min-width: 2500px) {
    width: 56dvw;
    top: -90px;
    --cutout-x: 43%;
    --cutout-height: 106px;
    --picture-radius: ${({ theme }) => theme.borderRadius.large};
  }
  @media (min-width: 3000px) {
    --cutout-x: 40%;
  }
  @media (min-width: 3400px) {
    --cutout-x: 37%;
    --picture-radius: ${({ theme }) => theme.borderRadius.xlarge};
  }
  @media (min-width: 3800px) {
    width: 52dvw;
    top: -140px;
    --cutout-height: 156px;
    --cutout-x: 44%;
  }
  @media (min-width: 4500px) {
    --cutout-x: 40%;
  }
  @media (min-width: 5000px) {
    --cutout-x: 38%;
  }
`;

export const PictureLower = styled.div`
  position: absolute;
  z-index: 0;
  inset: var(--cutout-height) 0 0;
  overflow: hidden;
  border-radius: var(--picture-radius);

  img {
    position: absolute;
    top: calc(var(--cutout-height) * -1);
    left: 0;
    width: 100%;
    height: calc(100% + var(--cutout-height));
    max-width: none;
    object-fit: cover;
  }

  @media (max-width: 1024px) {
    inset: 0;

    img {
      top: 0;
      height: 100%;
    }
  }
`;

export const PictureUpper = styled.div`
  position: absolute;
  z-index: 1;
  inset: 0 0 0 var(--cutout-x);
  overflow: hidden;
  border-radius: var(--picture-radius);

  img {
    position: absolute;
    top: 0;
    right: 0;
    width: calc(100% / 0.58);
    height: 100%;
    max-width: none;
    object-fit: cover;
  }

  @media (max-width: 1024px) {
    display: none;
  }

  @media (min-width: 2500px) {
    img {
      width: calc(100% / 0.57);
    }
  }
  @media (min-width: 3000px) {
    img {
      width: calc(100% / 0.6);
    }
  }
  @media (min-width: 3400px) {
    img {
      width: calc(100% / 0.63);
    }
  }
  @media (min-width: 3800px) {
    img {
      width: calc(100% / 0.56);
    }
  }
  @media (min-width: 4500px) {
    img {
      width: calc(100% / 0.6);
    }
  }
  @media (min-width: 5000px) {
    img {
      width: calc(100% / 0.62);
    }
  }
`;

export const PictureInternalCorner = styled.span`
  position: absolute;
  z-index: 2;
  inset: 0;
  background-image: var(--picture-image);
  background-position: center;
  background-size: cover;
  mask-image: radial-gradient(
    circle at 0 0,
    transparent 0 var(--picture-radius),
    #000 20.5px
  );
  mask-position: calc(var(--cutout-x) - 11.6px)
    calc(var(--cutout-height) - var(--picture-radius));
  mask-repeat: no-repeat;
  mask-size: var(--picture-radius) var(--picture-radius);
  -webkit-mask-image: radial-gradient(
    circle at 0 0,
    transparent 0 var(--picture-radius),
    #000 20.5px
  );
  -webkit-mask-position: calc(var(--cutout-x) - 11.6px)
    calc(var(--cutout-height) - var(--picture-radius));
  -webkit-mask-repeat: no-repeat;
  -webkit-mask-size: var(--picture-radius) var(--picture-radius);

  @media (max-width: 1024px) {
    display: none;
  }

  @media (min-width: 2500px) {
    mask-position: calc(var(--cutout-x) - 16.2px)
      calc(var(--cutout-height) - var(--picture-radius));
    -webkit-mask-position: calc(var(--cutout-x) - 16.2px)
      calc(var(--cutout-height) - var(--picture-radius));
    mask-size: var(--picture-radius) var(--picture-radius);
    mask-image: radial-gradient(
      circle at 0 0,
      transparent 0 var(--picture-radius),
      #000 31.5px
    );
    -webkit-mask-image: radial-gradient(
      circle at 0 0,
      transparent 0 var(--picture-radius),
      #000 31.5px
    );
  }
`;

export const TextLine = styled.div`
  position: relative;
  z-index: 2;
  display: flex;
  gap: ${({ theme }) => theme.spacing.small};
  margin-bottom: 50px;

  @media (max-width: 1600px) {
    max-width: 49%;
    margin-bottom: ${({ theme }) => theme.spacing.regular};
  }

  @media (min-width: 1281px) and (max-width: 1530px) {
    max-width: 46%;
  }

  @media (max-width: 1280px) {
    max-width: 53%;
  }

  @media (max-width: 1024px) {
    max-width: 100%;
    order: 3;
    margin-bottom: 0;
    gap: ${({ theme }) => theme.spacing.xsmall};
  }

  @media (max-width: 520px) {
    box-sizing: border-box;
    padding: 0 ${({ theme }) => theme.padding.small};
  }

  @media (min-width: 2500px) {
    margin-bottom: 40px;
  }
  @media (min-width: 2500px) and (max-height: 1080px) {
    margin-bottom: 10px;
  }
  @media (min-width: 3000px) {
    gap: ${({ theme }) => theme.spacing.regular};
  }
  @media (min-width: 3400px) {
    gap: 30px;
  }
  @media (min-height: 2000px) {
    margin-bottom: 120px;
  }
`;

export const Text = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.typography.body.fontSize};
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  line-height: 1.35;

  @media (min-width: 1025px) and (max-width: 1519px) and (max-height: 768px) {
    font-size: ${({ theme }) => theme.typography.medium.fontSize};
    line-height: 1.3;
  }

  @media (max-width: 520px) {
    font-size: ${({ theme }) => theme.typography.medium.fontSize};
    line-height: 1.3;
  }

  @media (min-width: 2500px) {
    font-size: 1.75rem;
  }
  @media (min-width: 2500px) and (max-height: 1080px) {
    font-size: 1.7rem;
  }
  @media (min-width: 3000px) {
    font-size: 2rem;
  }
  @media (min-width: 3400px) {
    font-size: 2.25rem;
  }
  @media (min-width: 3800px) {
    font-size: 3rem;
  }
  @media (min-width: 4500px) {
    font-size: 3.25rem;
  }
  @media (min-width: 5000px) {
    font-size: 3.75rem;
  }
`;

export const Star = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.typography.h1.fontSize};
  font-weight: ${({ theme }) => theme.fontWeight.light};
  line-height: 1;

  @media (min-width: 1025px) and (max-width: 1519px) and (max-height: 768px) {
    font-size: ${({ theme }) => theme.typography.h2.fontSize};
  }

  @media (max-width: 1024px) {
    font-size: ${({ theme }) => theme.typography.h2.fontSize};
  }

  @media (min-width: 2500px) {
    font-size: 4rem;
  }
  @media (min-width: 3000px) {
    font-size: 4.25rem;
  }
  @media (min-width: 3400px) {
    font-size: 4.5rem;
  }
  @media (min-width: 3800px) {
    font-size: 5rem;
  }
  @media (min-width: 4500px) {
    font-size: 5.5rem;
  }
  @media (min-width: 5000px) {
    font-size: 6rem;
  }
`;

export const TextBrake = styled.br`
  @media (max-width: 1520px) {
    display: none;
  }
`;

export const StatistcsCardTextBrake = styled.br`
  @media (max-width: 768px) {
    display: none;
  }
`;

export const Cards = styled.div`
  position: relative;
  z-index: 2;
  display: grid;
  grid-template-columns: repeat(3, 293px);
  gap: ${({ theme }) => theme.spacing.regular};

  @media (min-width: 1025px) and (max-width: 1519px) and (max-height: 768px) {
    width: min(72%, 900px);
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: ${({ theme }) => theme.spacing.small};
  }

  @media (max-width: 1024px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: ${({ theme }) => theme.spacing.small};
    order: 4;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    order: 5;
    box-sizing: border-box;
    width: 100%;
    padding: 0 ${({ theme }) => theme.padding.small};
  }

  @media (min-width: 2500px) {
    grid-template-columns: repeat(3, 440px);
  }
  @media (min-width: 3000px) {
    grid-template-columns: repeat(3, 16dvw);
    gap: 24px;
  }
  @media (min-width: 3400px) {
    grid-template-columns: repeat(3, 17dvw);
  }
  @media (min-width: 3800px) {
    grid-template-columns: repeat(3, 20dvw);
    gap: ${({ theme }) => theme.spacing.medium};
  }
`;

export const ActionRow = styled.div`
  position: relative;
  z-index: 2;
  width: fit-content;

  @media (min-width: 1025px) and (max-width: 1519px) and (max-height: 768px) {
    > button {
      font-size: ${({ theme }) => theme.typography.medium.fontSize};
    }

    > button > span:first-child {
      padding: ${({ theme }) => theme.padding.buttonMobile};
    }

    > button > span:last-child {
      min-width: 48px;
    }
  }

  @media (max-width: 1024px) {
    order: 5;
  }

  @media (max-width: 520px) {
    order: 4;
    width: 100%;
    box-sizing: border-box;
    padding: 0 ${({ theme }) => theme.padding.small};

    > button {
      width: 100%;
      inline-size: 100%;
      max-width: none;
      align-self: stretch;
    }

    > button > span:first-child {
      flex: 1;
    }
  }
`;

export const StatisticsSection = styled.section`
  box-sizing: border-box;
  padding: ${({ theme }) => theme.spacing.spacingDesktop}
    ${({ theme }) => theme.padding.medium} 0;
  scroll-margin-top: 72px;
  justify-items: center;

  @media (max-width: 1024px) {
    padding: ${({ theme }) => theme.spacing.spacingTablet}
      ${({ theme }) => theme.padding.regular} 0;
  }

  @media (max-width: 520px) {
    padding: ${({ theme }) => theme.spacing.large} 0 0;
    scroll-margin-top: 60px;
  }

  @media (min-width: 2500px) {
    padding: 180px ${({ theme }) => theme.padding.large} 0;
  }
  @media (min-width: 3000px) {
    padding: 200px ${({ theme }) => theme.padding.large} 0;
  }
  @media (min-width: 3500px) {
    padding: 220px 1.5dvw 0;
  }
  @media (min-width: 3800px) {
    padding: 260px 1.5dvw 0;
  }
  @media (min-width: 4500px) {
    padding: 8dvw 1.5dvw 0;
  }
`;

export const Statistics = styled.div`
  display: flex;
  box-sizing: border-box;
  justify-content: space-between;
  padding: ${({ theme }) => theme.padding.regular}
    ${({ theme }) => theme.padding.large};
  background-color: ${({ theme }) => theme.colors.darkBlue};
  border-radius: ${({ theme }) => theme.borderRadius.regular};
  color: ${({ theme }) => theme.colors.offWhite};
  width: 80vw;

  @media (max-width: 1520px) {
    width: 100%;
  }
  @media (max-width: 850px) {
    gap: ${({ theme }) => theme.spacing.regular};
  }
  @media (max-width: 750px) {
    flex-direction: column;
    align-items: center;
    gap: ${({ theme }) => theme.spacing.medium};
  }
  @media (max-width: 520px) {
    padding: ${({ theme }) => theme.padding.large}
      ${({ theme }) => theme.padding.regular};
  }

  @media (min-width: 2500px) {
    padding: ${({ theme }) => theme.padding.large};
    border-radius: ${({ theme }) => theme.borderRadius.medium};
  }
  @media (min-width: 3400px) {
    border-radius: ${({ theme }) => theme.borderRadius.large};
    padding: 52px;
  }
  @media (min-width: 4500px) {
    border-radius: ${({ theme }) => theme.borderRadius.xlarge};
    padding: 64px;
  }
`;

export const StatisticsTextBlock = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.regular};
  align-items: center;

  @media (max-width: 1220px) {
    gap: ${({ theme }) => theme.spacing.small};
  }
  @media (max-width: 900px) {
    flex-direction: column;
  }
  @media (max-width: 750px) {
    flex-direction: row;
    gap: ${({ theme }) => theme.spacing.regular};
  }
  @media (max-width: 275px) {
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing.small};
  }

  @media (min-width: 3400px) {
    gap: ${({ theme }) => theme.spacing.medium};
  }
`;

export const StatisticsNumber = styled.span`
  font-size: 4rem;
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  line-height: 1;

  @media (max-width: 1220px) {
    font-size: ${({ theme }) => theme.typography.h1.fontSize};
  }
  @media (max-width: 750px) {
    min-width: 110px;
    text-align: center;
  }

  @media (min-width: 2500px) {
    font-size: 5rem;
  }
  @media (min-width: 3400px) {
    font-size: 5.5rem;
  }
  @media (min-width: 3800px) {
    font-size: 6.5rem;
  }
  @media (min-width: 4500px) {
    font-size: 7rem;
  }
  @media (min-width: 5000px) {
    font-size: 8.5rem;
  }
`;

export const StatisticsText = styled.span`
  font-size: ${({ theme }) => theme.typography.body.fontSize};
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  line-height: 1.3rem;
  max-width: 180px;

  @media (max-width: 1220px) {
    font-size: ${({ theme }) => theme.typography.medium.fontSize};
    max-width: 140px;
  }
  @media (max-width: 900px) {
    text-align: center;
  }
  @media (max-width: 750px) {
    text-align: left;
  }
  @media (max-width: 275px) {
    text-align: center;
  }

  @media (min-width: 2500px) {
    font-size: 1.75rem;
    line-height: 2rem;
    max-width: 275px;
  }
  @media (min-width: 3400px) {
    font-size: 2.25rem;
    line-height: 2.5rem;
    max-width: 350px;
  }
  @media (min-width: 3800px) {
    font-size: 3rem;
    line-height: 3.25rem;
    max-width: 450px;
  }
  @media (min-width: 4500px) {
    font-size: 3.25rem;
    line-height: 3.5rem;
    max-width: 500px;
  }
  @media (min-width: 5000px) {
    font-size: 3.75rem;
    line-height: 4rem;
    max-width: 550px;
  }
`;

export const AboutUsSection = styled.section`
  display: flex;
  gap: ${({ theme }) => theme.spacing.medium};
  min-height: 70dvh;
  box-sizing: border-box;
  padding: ${({ theme }) => theme.spacing.spacingDesktop}
    ${({ theme }) => theme.padding.medium};
  scroll-margin-top: 72px;

  @media (max-width: 1520px) {
    flex-direction: column-reverse;
    gap: ${({ theme }) => theme.spacing.regular};
  }

  @media (max-width: 1024px) {
    padding: ${({ theme }) => theme.spacing.spacingTablet}
      ${({ theme }) => theme.padding.regular};
  }

  @media (max-width: 520px) {
    padding: ${({ theme }) => theme.spacing.large} 0;
    scroll-margin-top: 60px;
  }

  @media (min-width: 2500px) {
    padding: 200px ${({ theme }) => theme.padding.large};
  }
  @media (min-width: 3500px) {
    padding: 260px 1.5dvw;
  }
  @media (min-width: 3800px) {
    padding: 300px 1.5dvw;
  }
  @media (min-width: 4500px) {
    padding: 8dvw 1.5dvw;
  }
`;

export const AboutUsSectionText = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.medium};
  width: 63.542vw;

  @media (max-width: 1520px) {
    width: 100%;
  }
  @media (max-width: 520px) {
    padding: 0 ${({ theme }) => theme.padding.small};
  }

  @media (min-width: 3800px) {
    gap: ${({ theme }) => theme.spacing.large};
  }
`;

export const AboutUsHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xsmall};

  @media (max-width: 520px) {
    h2 {
      font-size: ${({ theme }) => theme.typography.body.fontSize};
    }
  }
`;

export const AboutUsContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.medium};
  height: 100%;

  @media (max-width: 1722px) {
    gap: ${({ theme }) => theme.spacing.regular};
  }

  @media (min-width: 3800px) {
    gap: ${({ theme }) => theme.spacing.large};
  }
`;

export const AboutUsImage = styled.div<{ $image: string }>`
  display: flex;
  position: relative;
  align-items: center;
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  width: 36.458vw;
  height: 86dvh;
  overflow: hidden;

  img {
    position: absolute;
    right: 0;
    width: 120%;

    @media (max-width: 1520px) {
      width: 100%;
    }
    @media (max-width: 900px) {
      width: 120%;
    }
    @media (max-width: 450px) {
      width: 130%;
    }
    @media (max-height: 870px) {
      width: 130%;
    }
    @media (max-height: 768px) {
      width: 100%;
    }
    @media (max-width: 580px) {
      width: 150%;
    }
    @media (max-width: 400px) and (max-height: 800px) {
      width: 120%;
    }
  }

  @media (max-width: 1520px) {
    width: 100%;
    height: 120dvh;
  }
  @media (max-width: 900px) {
    width: 100%;
    height: 90dvh;
  }
  @media (max-width: 520px) {
    width: 100%;
    height: 68.75dvh;
  }
  @media (max-width: 420px) {
    height: 60dvh;
  }
  @media (max-height: 900px) {
    height: 100dvh;
  }
  @media (max-height: 870px) {
    height: 110dvh;
  }
  @media (max-width: 420px) and (max-height: 920px) {
    height: 80dvh;
  }
  @media (max-height: 768px) {
    width: 100%;
    height: 170dvh;
  }
  @media (max-width: 540px) and (max-height: 730px) {
    height: 100dvh;
  }
  @media (max-width: 400px) and (max-height: 800px) {
    height: 80dvh;
  }

  @media (min-width: 2500px) {
    height: 42dvw;
    border-radius: ${({ theme }) => theme.borderRadius.large};
  }
  @media (min-width: 3000px) {
    height: 38dvw;
  }
  @media (min-width: 3400px) {
    border-radius: ${({ theme }) => theme.borderRadius.xlarge};
  }
  @media (min-width: 3800px) and (min-height: 2000px) {
    height: 41dvw;
  }
`;

export const AboutUsWorkBox = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.regular};
  padding: ${({ theme }) => theme.spacing.regular};
  background-color: ${({ theme }) => theme.colors.darkBlue};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  font-size: ${({ theme }) => theme.typography.body.fontSize};
  font-weight: ${({ theme }) => theme.fontWeight.medium};

  @media (max-width: 520px) {
    font-size: ${({ theme }) => theme.typography.medium.fontSize};
    padding: ${({ theme }) => theme.spacing.regular} 15px;
  }

  @media (min-width: 2500px) {
    gap: ${({ theme }) => theme.spacing.medium};
  }
  @media (min-width: 3000px) {
    padding: ${({ theme }) => theme.padding.medium};
  }
  @media (min-width: 3400px) {
    padding: ${({ theme }) => theme.padding.large};
    border-radius: ${({ theme }) => theme.borderRadius.large};
  }
  @media (min-width: 4500px) {
    gap: 52px;
    padding: 52px;
  }
`;

export const AboutUsDesktopText = styled.div`
  display: flex;

  @media (max-width: 1722px) {
    display: none;
  }
`;

export const AboutUsMobileText = styled.div`
  display: none;

  @media (max-width: 1722px) {
    display: flex;
  }
`;

export const WorksHeader = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.regular};
  align-items: center;
  color: ${({ theme }) => theme.colors.offWhite};

  p {
    margin: 0;
    font-weight: ${({ theme }) => theme.fontWeight.bold};
    text-transform: uppercase;
  }

  @media (max-width: 520px) {
    gap: ${({ theme }) => theme.spacing.small};
  }

  @media (min-width: 2500px) {
    p {
      font-size: 1.75rem;
      word-spacing: 6px;
    }
  }
  @media (min-width: 3000px) {
    p {
      font-size: 2rem;
    }
  }
  @media (min-width: 3400px) {
    p {
      font-size: 2.5rem;
      word-spacing: 8px;
    }
  }
  @media (min-width: 3800px) {
    gap: ${({ theme }) => theme.spacing.medium};
    p {
      font-size: 3rem;
    }
  }
  @media (min-width: 4500px) {
    p {
      font-size: 3.25rem;
    }
  }
  @media (min-width: 5000px) {
    p {
      font-size: 3.75rem;
    }
  }
`;

export const HeaderIcon = styled.span<{ $backgroundColor: string | undefined }>`
  display: flex;
  background-color: ${({ $backgroundColor, theme }) =>
    $backgroundColor ?? theme.colors.lightGreyBlue};
  border-radius: 100px;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;

  svg {
    width: 32px;
    height: 32px;
  }

  @media (max-width: 520px) {
    width: 32px;
    height: 32px;

    svg {
      width: 24px;
      height: 24px;
    }
  }

  @media (min-width: 2500px) {
    width: 52px;
    height: 52px;

    svg {
      width: 42px;
      height: 42px;
    }
  }
  @media (min-width: 3400px) {
    width: 68px;
    height: 68px;

    svg {
      width: 54px;
      height: 54px;
    }
  }
  @media (min-width: 3800px) {
    width: 76px;
    height: 76px;

    svg {
      width: 64px;
      height: 64px;
    }
  }
  @media (min-width: 4500px) {
    width: 82px;
    height: 82px;

    svg {
      width: 68px;
      height: 68px;
    }
  }
  @media (min-width: 5000px) {
    width: 92px;
    height: 92px;

    svg {
      width: 76px;
      height: 76px;
    }
  }
`;

export const WorksColumn = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.small};
  align-items: center;

  @media (max-width: 730px) {
    align-items: start;
  }
  @media (max-width: 385px) {
    width: 100%;
  }

  @media (min-width: 2500px) {
    gap: 12px;
  }
  @media (min-width: 3400px) {
    gap: 20px;
  }
  @media (min-width: 3800px) {
    gap: 24px;
  }
  @media (min-width: 4500px) {
    gap: 30px;
  }
`;

export const WorksLine = styled.div`
  box-sizing: border-box;
  display: flex;
  gap: ${({ theme }) => theme.spacing.small};
  justify-content: center;

  @media (max-width: 730px) {
    flex-direction: column;
  }
  @media (max-width: 385px) {
    width: 100%;
  }

  @media (min-width: 2500px) {
    gap: 12px;
  }
  @media (min-width: 3400px) {
    gap: 20px;
  }
  @media (min-width: 3800px) {
    gap: 24px;
  }
  @media (min-width: 4500px) {
    gap: 30px;
  }
`;

export const WorkItem = styled.div`
  display: flex;
  padding: 8px ${({ theme }) => theme.padding.regular};
  background-color: ${({ theme }) => theme.colors.background};
  border-radius: 999px;
  color: ${({ theme }) => theme.colors.darkBlue};
  align-items: center;
  justify-content: center;
  text-align: center;
  width: fit-content;

  @media (max-width: 520px) {
    padding: 5px 12px;
  }
  @media (max-width: 385px) {
    width: auto;
  }

  @media (min-width: 2500px) {
    padding: ${({ theme }) => theme.padding.small}
      ${({ theme }) => theme.padding.medium};
    font-size: 1.75rem;
  }
  @media (min-width: 3000px) {
    font-size: 2rem;
  }
  @media (min-width: 3400px) {
    font-size: 2.375rem;
  }
  @media (min-width: 3800px) {
    font-size: 2.75rem;
    padding: 16px 34px;
  }
  @media (min-width: 4500px) {
    font-size: 3.25rem;
  }
  @media (min-width: 5000px) {
    font-size: 3.5rem;
    padding: ${({ theme }) => theme.padding.regular}
      ${({ theme }) => theme.padding.large};
  }
`;

export const WorkItemBlue = styled(WorkItem)`
  @media (max-width: 730px) {
    background-color: #d4e1ef;
  }
`;

export const AboutUsCard = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.large};
  padding: ${({ theme }) => theme.spacing.regular};
  padding-bottom: 120px;
  align-items: flex-end;
  background-color: ${({ theme }) => theme.colors.grey};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  color: ${({ theme }) => theme.colors.darkBlue};
  height: fit-content;
  align-self: flex-end;

  @media (max-width: 1722px) {
    gap: ${({ theme }) => theme.spacing.regular};
    padding-bottom: ${({ theme }) => theme.spacing.regular};
    height: 100%;
  }

  @media (max-width: 1520px) {
    gap: ${({ theme }) => theme.spacing.medium};
    padding-bottom: ${({ theme }) => theme.spacing.large};
    width: 70%;
  }

  @media (max-width: 1440px) {
    width: 80%;
  }

  @media (max-width: 1024px) {
    width: auto;
  }

  @media (min-width: 3000px) {
    padding: ${({ theme }) => theme.padding.medium};
    padding-bottom: 120px;
  }
  @media (min-width: 3400px) {
    padding: ${({ theme }) => theme.padding.large};
    border-radius: ${({ theme }) => theme.borderRadius.large};
    padding-bottom: 120px;
  }
  @media (min-width: 4500px) {
    padding: 52px;
    padding-bottom: 120px;
  }
`;

export const AboutUsCardText = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.small};

  h1 {
    margin: 0;
    line-height: 1;
    font-size: ${({ theme }) => theme.typography.h1.fontSize};
    font-weight: ${({ theme }) => theme.fontWeight.light};

    // @media (max-width: 520px) {
    //   font-size: 2.5rem;
    // }
    @media (min-width: 2500px) {
      font-size: 4rem;
    }
    @media (min-width: 3400px) {
      font-size: 5.25rem;
    }
  }
  @media (min-width: 3000px) {
    gap: ${({ theme }) => theme.spacing.regular};
  }
  @media (min-width: 3400px) {
    font-size: 30px;
  }
`;

export const AboutUsCards = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.regular};
  height: 100%;

  @media (max-width: 1722px) {
    flex-direction: column;
  }
  @media (min-width: 3000px) {
    gap: 24px;
  }
  @media (min-width: 3800px) {
    gap: 30px;
  }
`;

export const AboutUsCardsColumn = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex: 2.5;

  @media (max-width: 1828px) {
    flex: 3;
  }

  @media (max-width: 1722px) {
    gap: 0;
    flex: 1;
  }
`;

export const AboutUsCardsLine = styled.div`
  display: flex;
  flex: 1.5;
  gap: ${({ theme }) => theme.spacing.regular};

  @media (max-width: 1722px) {
    flex: 1;
    align-items: center;
  }

  @media (max-width: 650px) {
    flex-direction: column-reverse;
  }
`;

export const AboutUsTextBrake = styled.br`
  @media (max-width: 1722px) {
    display: none;
  }
`;

export const RecognitionSection = styled.section`
  display: flex;
  gap: ${({ theme }) => theme.spacing.large};
  min-height: 70dvh;
  box-sizing: border-box;
  padding: ${({ theme }) => theme.padding.xlarge} 0;
  justify-content: center;
  background-color: #cddded66;
  border-radius: ${({ theme }) => theme.borderRadius.xlarge};

  @media (max-width: 1366px) {
    gap: ${({ theme }) => theme.spacing.medium};
  }

  @media (max-width: 1024px) {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(260px, 0.9fr);
    grid-template-areas:
      "heading heading"
      "copy copy"
      "image trust"
      "image button";
    padding: ${({ theme }) => theme.padding.large}
      ${({ theme }) => theme.padding.regular};
    column-gap: ${({ theme }) => theme.spacing.regular};
    row-gap: ${({ theme }) => theme.spacing.regular};
    border-radius: ${({ theme }) => theme.borderRadius.large};
    align-items: start;
  }

  @media (max-width: 520px) {
    grid-template-columns: minmax(0, 1fr);
    grid-template-areas:
      "heading"
      "trust"
      "copy"
      "image"
      "button";
    padding: ${({ theme }) => theme.padding.large}
      ${({ theme }) => theme.padding.small};
    gap: ${({ theme }) => theme.spacing.regular};
    border-radius: ${({ theme }) => theme.borderRadius.medium};
  }

  @media (min-width: 2500px) {
    padding: 100px ${({ theme }) => theme.padding.large};
  }
  @media (min-width: 3500px) {
    padding: 130px 1.5dvw;
  }
  @media (min-width: 3800px) {
    padding: 150px 1.5dvw;
  }
  @media (min-width: 4500px) {
    padding: 4dvw 1.5dvw;
  }
`;

export const RecognitionImage = styled.div<{ $image: string }>`
  box-sizing: border-box;
  display: flex;
  position: relative;
  align-items: center;
  justify-content: center;
  background-color: ${({ theme }) => theme.colors.white};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  height: 80dvh;
  overflow: hidden;
  padding: ${({ theme }) => theme.padding.small};
  cursor: zoom-in;

  img {
    height: 100%;
  }

  @media (max-width: 1366px) {
    height: 52dvw;
  }

  @media (max-width: 1024px) {
    grid-area: image;
    align-self: stretch;
    width: 100%;
    max-width: none;
    height: auto;
    min-height: 0;
    aspect-ratio: 0.7 / 1;
    padding: ${({ theme }) => theme.padding.xsmall};

    img {
      width: 100%;
      height: 100%;
      object-fit: contain;
    }
  }

  @media (max-width: 520px) {
    aspect-ratio: 0.69 / 1;
  }

  @media (min-width: 2500px) {
    height: 42dvw;
    border-radius: ${({ theme }) => theme.borderRadius.large};
  }
  @media (min-width: 3000px) {
    height: 38dvw;
  }
  @media (min-width: 3400px) {
    border-radius: ${({ theme }) => theme.borderRadius.xlarge};
  }
`;

export const RecognitionTextOuterBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  width: 50vw;

  @media (max-width: 1024px) {
    display: contents;
  }

  @media (min-width: 2500px) {
    width: 60dvw;
  }
`;

export const RecognitionTextBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.medium};
  max-width: 750px;
  height: 100%;
  align-self: flex-start;

  @media (max-width: 1024px) {
    display: contents;
  }

  @media (min-width: 2500px) {
    max-width: 42dvw;
  }
  @media (min-width: 3400px) {
    margin-top: -82px;
    max-width: 50dvw;
    gap: ${({ theme }) => theme.spacing.large};
  }
`;

export const RecognitionHeading = styled.div`
  @media (max-width: 1024px) {
    grid-area: heading;
    text-align: center;
    margin-bottom: ${({ theme }) => theme.spacing.regular};
  }

  @media (max-width: 520px) {
    text-align: left;
  }
`;

export const RecognitionText = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.regular};
  text-align: left;

  @media (max-width: 1024px) {
    grid-area: copy;
  }

  @media (max-width: 520px) {
    gap: ${({ theme }) => theme.spacing.small};
  }

  @media (min-width: 3000px) {
    gap: ${({ theme }) => theme.spacing.medium};
  }
  @media (min-width: 3400px) {
    gap: ${({ theme }) => theme.spacing.large};
  }
`;

export const RecognitionButtonBox = styled.div`
  display: flex;
  margin-top: auto;
  width: 100%;
  justify-content: flex-end;

  @media (max-width: 1280px) {
    margin-top: 0;
  }

  @media (max-width: 1024px) {
    grid-area: button;
    align-self: start;
    justify-content: flex-start;
  }

  @media (max-width: 520px) {
    width: 100%;

    > button {
      width: 100%;
      inline-size: 100%;
      max-width: none;
      align-self: stretch;
    }

    > button > span:first-child {
      flex: 1;
    }
  }
`;

export const TrustCardWrapper = styled.div`
  display: flex;
  flex-direction: column;

  @media (max-width: 1024px) {
    grid-area: trust;
    width: 100%;
    margin-top: ${({ theme }) => theme.spacing.large};
  }

  @media (max-width: 520px) {
    margin-top: 0;
  }
`;

export const TrustCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.regular};
  padding: ${({ theme }) => theme.padding.regular};
  border-radius: ${({ theme }) => theme.borderRadius.regular};
  background-color: ${({ theme }) => theme.colors.background};
  max-width: 333px;
  box-sizing: border-box;
  z-index: 2;

  p {
    font-size: ${({ theme }) => theme.typography.medium.fontSize};
    font-weight: ${({ theme }) => theme.typography.body.fontWeight};
    margin: 0;

    @media (max-width: 520px) {
      font-size: ${({ theme }) => theme.typography.regular.fontSize};
    }

    @media (min-width: 2500px) {
      font-size: 1.5rem;
    }
    @media (min-width: 3000px) {
      font-size: 1.75rem;
    }
    @media (min-width: 3400px) {
      font-size: 2.25rem;
    }
    @media (min-width: 3800px) {
      font-size: 2.5rem;
      gap: ${({ theme }) => theme.spacing.medium};
    }
    @media (min-width: 4500px) {
      font-size: 2.75rem;
    }
    @media (min-width: 5000px) {
      font-size: 3.25rem;
    }
  }

  svg {
    width: 20px;
    height: 20px;

    @media (min-width: 2500px) {
      width: 28px;
      height: 28px;
    }
    @media (min-width: 3000px) {
      width: 32px;
      height: 32px;
    }
    @media (min-width: 3400px) {
      width: 36px;
      height: 36px;
    }
    @media (min-width: 3800px) {
      width: 48px;
      height: 48px;
    }
    @media (min-width: 4500px) {
      width: 52px;
      height: 52px;
    }
    @media (min-width: 5000px) {
      width: 58px;
      height: 58px;
    }
  }

  @media (max-width: 1024px) {
    width: 100%;
    max-width: none;
  }

  @media (max-width: 520px) {
    gap: ${({ theme }) => theme.spacing.small};
  }

  @media (min-width: 2500px) {
    max-width: 500px;
    border-radius: ${({ theme }) => theme.borderRadius.medium};
    padding: 24px;
  }
  @media (min-width: 3000px) {
    max-width: 600px;
    padding: ${({ theme }) => theme.padding.medium};
    border-radius: ${({ theme }) => theme.borderRadius.medium};
  }
  @media (min-width: 3400px) {
    max-width: 730px;
    padding: ${({ theme }) => theme.padding.large};
    border-radius: ${({ theme }) => theme.borderRadius.large};
  }
  @media (min-width: 3800px) {
    max-width: 840px;
  }
  @media (min-width: 4500px) {
    max-width: 960px;
    padding: 52px;
  }
`;

export const TrustCardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;

  span {
    display: flex;
    width: 36px;
    height: 36px;
    background-color: ${({ theme }) => theme.colors.green};
    border-radius: 100px;
    align-items: center;
    justify-content: center;

    @media (max-width: 520px) {
      width: 32px;
      height: 32px;
    }

    @media (min-width: 2500px) {
      width: 42px;
      height: 42px;
    }
    @media (min-width: 2500px) {
      width: 52px;
      height: 52px;
    }
    @media (min-width: 3400px) {
      width: 62px;
      height: 62px;
    }
    @media (min-width: 3800px) {
      width: 72px;
      height: 72px;
    }
    @media (min-width: 4500px) {
      width: 76px;
      height: 76px;
    }
    @media (min-width: 5000px) {
      width: 82px;
      height: 82px;
    }
  }

  p {
    font-size: ${({ theme }) => theme.typography.body.fontSize};
    font-weight: ${({ theme }) => theme.fontWeight.bold};
    margin: 0;

    @media (max-width: 520px) {
      font-size: ${({ theme }) => theme.typography.medium.fontSize};
    }

    @media (min-width: 2500px) {
      font-size: 1.75rem;
    }
    @media (min-width: 3400px) {
      font-size: 2.25rem;
    }
    @media (min-width: 3800px) {
      font-size: 2.5rem;
    }
    @media (min-width: 4500px) {
      font-size: 2.75rem;
    }
    @media (min-width: 5000px) {
      font-size: 3.25rem;
    }
  }
`;

export const WorkingSince = styled.span`
  padding: ${({ theme }) => theme.padding.regular};
  border-radius: ${({ theme }) => theme.borderRadius.regular};
  background-color: #5d697c33;
  font-size: ${({ theme }) => theme.typography.medium.fontSize};
  font-weight: ${({ theme }) => theme.typography.body.fontWeight};
  width: fit-content;
  align-self: flex-end;
  margin-top: -10px;
  margin-right: -40px;
  z-index: 1;

  @media (max-width: 1024px) {
    margin-right: 0;
  }

  @media (max-width: 520px) {
    font-size: ${({ theme }) => theme.typography.regular.fontSize};
  }
  @media (min-width: 2500px) {
    font-size: 1.5rem;
  }
  @media (min-width: 3000px) {
    font-size: 1.75rem;
    padding: ${({ theme }) => theme.padding.medium};
    border-radius: ${({ theme }) => theme.borderRadius.medium};
    margin-right: -60px;
    margin-top: -20px;
  }
  @media (min-width: 3400px) {
    font-size: 2.25rem;
    padding: ${({ theme }) => theme.padding.large};
    border-radius: ${({ theme }) => theme.borderRadius.large};
  }
  @media (min-width: 3800px) {
    font-size: 2.5rem;
  }
  @media (min-width: 4500px) {
    font-size: 2.75rem;
    padding: 52px;
    margin-right: -80px;
    margin-top: -30px;
  }
  @media (min-width: 5000px) {
    font-size: 3.25rem;
  }
`;

export const ProductsSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.large};
  min-height: 70dvh;
  box-sizing: border-box;
  padding: ${({ theme }) => theme.spacing.spacingDesktop}
    ${({ theme }) => theme.padding.medium};
  align-items: center;
  scroll-margin-top: 72px;

  @media (max-width: 1024px) {
    padding: ${({ theme }) => theme.spacing.spacingTablet}
      ${({ theme }) => theme.padding.regular};
    gap: ${({ theme }) => theme.spacing.medium};
  }

  @media (max-width: 520px) {
    padding: ${({ theme }) => theme.spacing.large}
      ${({ theme }) => theme.padding.small};
    scroll-margin-top: 60px;
  }

  @media (min-width: 2500px) {
    padding: 200px ${({ theme }) => theme.padding.large};
  }
  @media (min-width: 3500px) {
    padding: 260px 1.5dvw;
  }
  @media (min-width: 3800px) {
    padding: 300px 1.5dvw;
    gap: ${({ theme }) => theme.spacing.spacingTablet};
  }
  @media (min-width: 4500px) {
    padding: 8dvw 1.5dvw;
    gap: ${({ theme }) => theme.spacing.spacingDesktop};
  }
`;

export const ProductsHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.padding.small};
  align-items: center;
  text-align: center;
  max-width: 610px;

  @media (min-width: 2500px) {
    max-width: 800px;
  }
  @media (min-width: 3000px) {
    max-width: 900px;
    gap: ${({ theme }) => theme.padding.regular};
  }
  @media (min-width: 3400px) {
    max-width: 38dvw;
    gap: ${({ theme }) => theme.padding.medium};
  }
`;

export const ProductContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.padding.regular};
  width: 100%;
  align-items: start;

  h2 {
    font-size: ${({ theme }) => theme.typography.h2.fontSize};
    font-weight: ${({ theme }) => theme.fontWeight.extrabold};
    margin: 0;
    text-transform: uppercase;
    word-spacing: ${({ theme }) => theme.spacing.xsmall};

    @media (max-width: 520px) {
      font-size: ${({ theme }) => theme.typography.body.fontSize};
    }

    @media (min-width: 2500px) {
      font-size: 2.75rem;
    }
    @media (min-width: 3400px) {
      font-size: 3.25rem;
    }
    @media (min-width: 3800px) {
      font-size: 3.75rem;
    }
    @media (min-width: 4500px) {
      font-size: 4.25rem;
    }
    @media (min-width: 5000px) {
      font-size: 4.75rem;
    }
  }

  @media (min-width: 3000px) {
    gap: ${({ theme }) => theme.padding.medium};
  }
  @media (min-width: 3800px) {
    gap: ${({ theme }) => theme.padding.large};
  }
`;

export const ProductCards = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: ${({ theme }) => theme.spacing.regular};
  width: 100%;

  @media (max-width: 1600px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  @media (max-width: 1180px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  // @media (max-width: 1024px) {
  //   grid-template-columns: 1fr;
  //   gap: ${({ theme }) => theme.spacing.regular};
  // }

  @media (max-width: 810px) {
    grid-template-columns: 1fr;
    gap: ${({ theme }) => theme.spacing.regular};
  }

  @media (min-width: 3000px) {
    gap: 24px;
  }
  @media (min-width: 3800px) {
    gap: 30px;
  }
`;

export const ApplicationSection = styled.section`
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  padding: ${({ theme }) => theme.spacing.large}
    ${({ theme }) => theme.padding.medium};
  align-items: center;
  scroll-margin-top: 72px;
  border-radius: ${({ theme }) => theme.borderRadius.xlarge};
  background-color: ${({ theme }) => theme.colors.darkBlue};

  @media (max-width: 1024px) {
    border-radius: ${({ theme }) => theme.borderRadius.large};
    padding: ${({ theme }) => theme.padding.large};
  }
  @media (max-width: 520px) {
    border-radius: ${({ theme }) => theme.borderRadius.medium};
    padding: ${({ theme }) => theme.padding.medium}
      ${({ theme }) => theme.spacing.small};
  }

  @media (min-width: 2500px) {
    padding: 100px ${({ theme }) => theme.padding.large};
  }
  @media (min-width: 3500px) {
    padding: 130px 1.5dvw;
  }
  @media (min-width: 3800px) {
    padding: 150px 1.5dvw;
  }
  @media (min-width: 4500px) {
    padding: 4dvw 1.5dvw;
  }
`;

export const ContactsSection = styled.section`
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  padding-top: ${({ theme }) => theme.spacing.spacingDesktop};

  @media (max-width: 1024px) {
    padding-top: ${({ theme }) => theme.spacing.spacingTablet};
  }
  @media (max-width: 520px) {
    padding-top: ${({ theme }) => theme.spacing.large};
  }

  @media (min-width: 2500px) {
    padding-top: 200px;
  }
  @media (min-width: 3500px) {
    padding-top: 260px;
  }
  @media (min-width: 3800px) {
    padding-top: 300px;
  }
  @media (min-width: 4500px) {
    padding-top: 8dvw;
  }
`;

export const FooterSection = styled.section`
  background-color: ${({ theme }) => theme.colors.grey};
`;

export const ContactsFooterReveal = styled.section`
  display: flex;
  flex-direction: column;
  width: 100%;
`;
